import { defineStore } from 'pinia';
import http from '../api/httpRequest.js'
import yunxinIM from '../api/yunxinIM.js'

function getGroupPrimaryId(group = {}) {
	return group.teamId || group.imGroupId || group.imTeamId || group.id;
}

function getGroupAliasIds(group = {}) {
	return [group.id, group.teamId, group.imGroupId, group.imTeamId]
		.filter(id => id !== undefined && id !== null && id !== '');
}

function getTeamName(team = {}) {
	return team.name || team.teamName || team.groupName || team.showName || team.showGroupName || '';
}

function getTeamAvatar(team = {}) {
	return team.avatar || team.icon || team.teamAvatar || team.headImage || team.headImageThumb || '';
}

function normalizeSdkTeam(team = {}) {
	const teamId = String(team.teamId || team.id || '');
	const name = getTeamName(team) || teamId;
	const avatar = getTeamAvatar(team);
	return {
		...team,
		id: teamId,
		teamId,
		teamType: Number(team.teamType || team.type || 1),
		name,
		showGroupName: team.remarkGroupName || name,
		headImage: avatar,
		headImageThumb: avatar,
		ownerId: team.ownerAccountId || team.owner || team.ownerId || '',
		notice: team.announcement || team.notice || '',
		intro: team.intro || team.introduction || '',
		remarkGroupName: team.remarkGroupName || '',
		isDnd: !!team.mute,
		isTop: !!team.stickTop,
		isAllMuted: !!team.chatBannedMode,
		sdkCreated: true,
		sourceTeam: team
	};
}

export default defineStore('groupStore', {
	state: () => {
		return {
			groups: [],// 数组用于展示，防止更新时不刷新
			groupMap: new Map(), // map用于查询，提高查询效率
			teamNotices: []
		}
	},
	actions: {
		setGroups(groups) {
			// 逐个群处理，保证ws重连后群成员不被清理
			groups.forEach(group => this.addGroup(group));
		},
		getTeamNoticeKey(notice) {
			return notice.id || `${notice.actionType}_${notice.teamId}_${notice.operatorAccountId}_${notice.timestamp || ''}`;
		},
		setTeamNotices(notices = []) {
			this.teamNotices = [];
			notices.forEach(notice => this.addTeamNotice(notice));
		},
		addTeamNotice(notice) {
			const key = this.getTeamNoticeKey(notice);
			const finalNotice = {
				...notice,
				id: key,
				read: !!notice.read,
				actionStatus: Number(notice.actionStatus || 0)
			};
			const index = this.teamNotices.findIndex(item => this.getTeamNoticeKey(item) === key);
			if (index >= 0) {
				this.teamNotices.splice(index, 1, { ...this.teamNotices[index], ...finalNotice });
			} else {
				this.teamNotices.unshift(finalNotice);
			}
		},
		setTeamNoticeRead(id) {
			const notice = this.teamNotices.find(item => this.getTeamNoticeKey(item) === id || item.id === id);
			if (notice) {
				notice.read = true;
			}
		},
		setAllTeamNoticeRead() {
			this.teamNotices.forEach(notice => {
				notice.read = true;
			});
		},
		updateTeamNoticeStatus(id, status) {
			const notice = this.teamNotices.find(item => this.getTeamNoticeKey(item) === id || item.id === id);
			if (notice) {
				notice.actionStatus = Number(status);
				notice.read = true;
			}
		},
		addGroup(group) {
			const primaryId = getGroupPrimaryId(group);
			if (!primaryId) {
				return;
			}
			const finalGroup = {
				...group,
				id: String(primaryId),
				teamId: group.teamId ? String(group.teamId) : String(primaryId)
			};
			if (this.groupMap.has(finalGroup.id)) {
				this.updateGroup(finalGroup)
			} else {
				finalGroup.members = Array.isArray(finalGroup.members) ? finalGroup.members : [];
				this.groups.unshift(finalGroup);
				getGroupAliasIds(finalGroup).forEach(id => this.groupMap.set(String(id), finalGroup));
			}
		},
		removeGroup(id) {
			let group = this.findGroup(id);
			group.quit = true;
		},
		updateGroup(group) {
			let g = this.findGroup(getGroupPrimaryId(group));
			if (!g) {
				return this.addGroup(group);
			}
			let members = g.members;
			if (group.sdkCreated && Array.isArray(group.members)) {
				// 云信群成员由 SDK 全量返回，需要替换旧列表才能刷新侧栏展示。
				members = group.members.length ? group.members : members;
			}
			// 旧后端群成员仍走增量刷新，避免普通群信息更新时清空成员。
			group.members = members;
			// 拷贝属性
			Object.assign(g, group);
			g.id = String(getGroupPrimaryId(g));
			g.teamId = g.teamId ? String(g.teamId) : g.id;
			getGroupAliasIds(g).forEach(id => this.groupMap.set(String(id), g));
		},
		updateTopMessage(id, topMessage) {
			let group = this.findGroup(id);
			if (group) {
				group.topMessage = topMessage;
			}
		},
		setDnd(id, isDnd) {
			let group = this.findGroup(id);
			if (group) {
				group.isDnd = isDnd;
			}
		},
		setTop(id, isTop) {
			let group = this.findGroup(id);
			if (group) {
				group.isTop = isTop;
			}
		},
		setMuted(id, isMuted) {
			let group = this.findGroup(id);
			if (group) {
				group.isMuted = isMuted;
			}
		},
		setAllMuted(id, isAllMuted) {
			let group = this.findGroup(id);
			if (group) {
				group.isAllMuted = isAllMuted;
			}
		},
		refreshMember(id) {
			let group = this.findGroup(id);
			// 成员最大版本号
			let maxVersion = 0;
			if (group.members) {
				group.members.forEach(m => maxVersion = Math.max(maxVersion, m.version));
			}
			http({
				url: `/group/members/${id}?version=${maxVersion}`,
				method: 'get'
			}).then(members => {
				if (!group.members.length) {
					// 全量更新
					group.members = members;
				} else {
					// 增量更新
					members.forEach(m1 => {
						let member = group.members.find(m2 => m1.userId == m2.userId);
						if (member) {
							Object.assign(member, m1);
						} else {
							group.members.push(m1)
						}
					})
					// 更新成员在线状态
					this.refreshMemberOnline(id);
				}
			});
		},
		refreshMemberOnline(id) {
			let group = this.findGroup(id);
			http({
				url: `/group/members/online/${id}`,
				method: 'get'
			}).then(userIds => {
				group.members.forEach(m => {
					// 更新成员在线状态
					m.online = userIds.some(userId => m.userId == userId)
				})
				// 重新排序
				this.refreshMmeberSort(id);
			})
		},
		refreshMmeberSort(id) {
			let group = this.findGroup(id);
			group.members.sort((m1, m2) => {
				// 在线的放前面
				if (m1.online && !m2.online) {
					return -1
				}
				if (!m1.online && m2.online) {
					return 1
				}
				// 群主在前面
				if (m1.userId == group.ownerId) {
					return -1;
				}
				if (m2.userId == group.ownerId) {
					return 1;
				}
				// 管理员在前面
				if (m1.isManager && !m2.isManager) {
					return -1;
				}
				if (!m1.isManager && m2.isManager) {
					return 1;
				}
				return 0;
			})
		},
		clear() {
			this.groups = [];
			this.groupMap.clear();
			this.teamNotices = [];
		},
		async loadSdkGroups() {
			const teams = await yunxinIM.getJoinedTeamList([1, 2]);
			const groups = await Promise.all(teams.map(async team => {
				const group = normalizeSdkTeam(team);
				const members = await yunxinIM.getTeamMemberList(group.teamId, group.teamType).catch(() => []);
				group.members = members;
				return group;
			}));
			this.setGroups(groups);
			return groups;
		},
		loadGroup() {
			return new Promise((resolve, reject) => {
				http({
					url: '/group/list',
					method: 'GET'
				}).then(async legacyGroups => {
					await this.loadSdkGroups().catch(error => {
						console.warn('[groupStore.loadGroup] 获取云信群列表失败，使用后端群列表兜底:', error);
						this.setGroups(legacyGroups);
					});
					const notices = await yunxinIM.getTeamJoinActionInfos([2], [0, 1, 2, 3]).catch(() => []);
					this.setTeamNotices(notices.map(notice => ({
						...notice,
						sourceActionInfo: notice
					})));
					resolve();
				}).catch((res) => {
					reject(res);
				})
			});
		}
	},
	getters: {
		findGroup: (state) => (id) => {
			return state.groupMap.get(id);
		},
		isGroup: (state) => (id) => {
			let group = state.findGroup(id);
			return group && !group.quit
		},
	}
});
